import { ref, onMounted, onUnmounted, nextTick, Ref, unref } from "vue";
import { useMainStore } from "@/mainStore";
import delayPromise from "./delayPromise";
import { FOCUS_QUERY, isElementVisible } from "./focusHelpers";

type TriggerElement = HTMLElement | string | null;
export type FocusTrapOptions = {
    triggerElement?: TriggerElement | Ref<TriggerElement>;
    withComponent?: boolean;
};
type ActiveFocusTrap = {
    lifecycleId: number;
    enabled: boolean;
};

let focusTrapLifecycleId = 0;
const activeFocusTraps: ActiveFocusTrap[] = [];

const isNewerEnabledFocusTrapActive = (lifecycleId: number) => activeFocusTraps.some(activeFocusTrap => activeFocusTrap.lifecycleId > lifecycleId && activeFocusTrap.enabled);
const registerActiveFocusTrap = (enabled: boolean) => {
    const lifecycleId = ++focusTrapLifecycleId;
    activeFocusTraps.push({ lifecycleId, enabled });
    return lifecycleId;
};
const updateActiveFocusTrapEnabled = (lifecycleId: number, enabled: boolean) => {
    const activeFocusTrap = activeFocusTraps.find(trap => trap.lifecycleId === lifecycleId);
    if (activeFocusTrap != null) {
        activeFocusTrap.enabled = enabled;
    }
};
const unregisterActiveFocusTrap = (lifecycleId: number) => {
    const activeTrapIndex = activeFocusTraps.findIndex(trap => trap.lifecycleId === lifecycleId);
    if (activeTrapIndex >= 0) {
        activeFocusTraps.splice(activeTrapIndex, 1);
    }
};

export function focusTrapSetup(focusTrapContainer: Ref<HTMLElement | null>, options: FocusTrapOptions = {}) {
    const withComponent = options.withComponent ?? true;
    const defaultTriggerElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const mainStore = useMainStore();
    const focusTrapEnabled = ref(true);
    let activeFocusTrapLifecycleId = 0;

    const focusTrapSetEnabled = (input: boolean) => {
        focusTrapEnabled.value = input;
        updateActiveFocusTrapEnabled(activeFocusTrapLifecycleId, input);
    };
    const handleTabKey = (event: KeyboardEvent) => {
        if (event.key !== "Tab" || !focusTrapEnabled.value) return;
        const containerElement = focusTrapContainer.value;
        if (containerElement == null) return;

        const focusableElements = Array.from(containerElement.querySelectorAll(FOCUS_QUERY)).filter(isElementVisible) as HTMLElement[];
        if (focusableElements.length === 0) {
            event.preventDefault();
            return;
        }

        const activeElement = document.activeElement as HTMLElement | null;
        let currentIndex = -1;
        if (activeElement && containerElement.contains(activeElement)) {
            currentIndex = focusableElements.findIndex(el => el === activeElement);
        }

        let nextIndex;
        if (event.shiftKey) {
            // Shift + Tab
            if (currentIndex <= 0) {
                nextIndex = focusableElements.length - 1;
            } else {
                nextIndex = currentIndex - 1;
            }
        } else {
            // Tab
            if (currentIndex === focusableElements.length - 1 || currentIndex === -1) {
                nextIndex = 0;
            } else {
                nextIndex = currentIndex + 1;
            }
        }
        event.preventDefault();
        focusableElements[nextIndex]?.focus();
    };
    const resolveFocusableTriggerElement = () => {
        const target = unref(options.triggerElement) ?? defaultTriggerElement;
        if (!target) return null;
        const element = typeof target === "string" ? document.querySelector(target) : target;
        return element instanceof HTMLElement && element.isConnected ? element : null;
    };
    const focusFirstElementInTrap = async () => {
        const containerElement = focusTrapContainer.value;
        if (containerElement == null) return;

        const focusableElements = Array.from(containerElement.querySelectorAll(FOCUS_QUERY)) as HTMLElement[];
        if (focusableElements.length === 0) return;

        await nextTick();
        const autofocusedElement = document.querySelector("[autofocus]") as HTMLElement | null;
        const targetElementToFocus = autofocusedElement != null && focusableElements.includes(autofocusedElement) ? autofocusedElement : focusableElements[0];
        targetElementToFocus.focus();
    };
    const focusToTrigger = async (disposeLifecycleId: number) => {
        await delayPromise(0);
        // Skip trigger focus restore while a newer trap is still active during modal handoff.
        if (isNewerEnabledFocusTrapActive(disposeLifecycleId)) return;
        resolveFocusableTriggerElement()?.focus();
    };

    let abortController: AbortController | null = null;
    const init = async () => {
        const containerElement = focusTrapContainer.value;
        if (containerElement == null) return;

        activeFocusTrapLifecycleId = registerActiveFocusTrap(focusTrapEnabled.value);
        abortController = new AbortController();
        document.addEventListener("keydown", handleTabKey, { signal: abortController.signal, capture: true });
        mainStore.toggleFocusTrapActiveModals(false);
        mainStore.addActiveModal(focusTrapEnabled);
        await focusFirstElementInTrap();
    };
    const dispose = () => {
        abortController?.abort();
        const wasTopActiveModal = mainStore.activeModals.lastIndexOf(focusTrapEnabled) === mainStore.activeModals.length - 1;
        mainStore.removeActiveModal(focusTrapEnabled);
        unregisterActiveFocusTrap(activeFocusTrapLifecycleId);
        if (wasTopActiveModal) {
            mainStore.toggleFocusTrapActiveModals(true);
        }
        focusToTrigger(activeFocusTrapLifecycleId);
    };

    if (withComponent) {
        onMounted(init);
        onUnmounted(dispose);
    }

    return {
        focusTrapSetEnabled,
        init,
        dispose,
    };
}
